/**
 * videoEffects — shared client-side video-effects engine for Bloq.
 *
 * The ONLY module that talks to Agora's Virtual Background + Beauty Effect
 * extensions. Both the Lobby and the Meeting Room use it, but each component
 * owns its OWN camera track + processors and its own lifecycle — this module
 * never owns a global "current track", never publishes, never navigates, and
 * never transfers a track between components. It only standardises effect
 * behaviour.
 *
 * Effects are 100% client-side and per-participant: they only alter the local
 * user's OUTGOING camera feed. The virtual-background WASM ships embedded
 * (base64) in the extension bundle, so `processor.init()` needs no external
 * asset URL and works offline / same-origin.
 */
import AgoraRTC from "agora-rtc-sdk-ng";
import VirtualBackgroundExtension from "agora-extension-virtual-background";
import BeautyExtension from "agora-extension-beauty-effect";

export const DEFAULT_VIDEO_EFFECTS = {
  background: "none", // "none" | "blur" | "image"
  blurDegree: 2, // 1 = low, 2 = medium, 3 = high
  imageDataUrl: null, // custom background (data URL) or preset src
  presetId: null, // id of a built-in preset when one is selected
  beautyEnabled: false,
  smoothnessLevel: 0.5,
  lighteningLevel: 0.6,
  rednessLevel: 0.1,
  sharpnessLevel: 0.3,
};

let _registered = false;
let _vbExt = null;
let _beautyExt = null;

// Track-scoped processor ownership so attaching twice to the same track is a
// no-op (protects against React remounts / double-attach).
const processorsByTrack = new WeakMap();

function ensureRegistered() {
  if (_registered) return;
  _vbExt = new VirtualBackgroundExtension();
  _beautyExt = new BeautyExtension();
  AgoraRTC.registerExtensions([_vbExt, _beautyExt]);
  _registered = true;
}

/**
 * Separate capability results — virtual background and beauty can differ, and
 * one being unavailable must NOT disable the other.
 */
export function checkVideoEffectsCompatibility() {
  const ua = navigator.userAgent || "";
  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
  const wasmOk = typeof WebAssembly === "object";
  let backgroundSupported = wasmOk && !isMobile;
  let beautySupported = wasmOk && !isMobile;
  let backgroundReason = null;
  let beautyReason = null;

  if (!wasmOk) {
    backgroundReason = beautyReason = "WebAssembly is not available in this browser";
  } else if (isMobile) {
    backgroundReason = beautyReason = "Video effects aren't supported on mobile browsers";
  } else {
    try {
      ensureRegistered();
      if (_vbExt && typeof _vbExt.checkCompatibility === "function") {
        backgroundSupported = !!_vbExt.checkCompatibility();
        if (!backgroundSupported) backgroundReason = "This browser can't run background effects";
      }
      if (_beautyExt && typeof _beautyExt.checkCompatibility === "function") {
        beautySupported = !!_beautyExt.checkCompatibility();
        if (!beautySupported) beautyReason = "This browser can't run the beauty filter";
      }
    } catch (e) {
      backgroundSupported = false;
      beautySupported = false;
      backgroundReason = beautyReason = "Video effects are unavailable";
    }
  }
  return {
    backgroundSupported,
    beautySupported,
    recommendedBrowser: backgroundSupported && beautySupported,
    backgroundReason,
    beautyReason,
  };
}

/** Back-compat boolean used to decide whether to show the effects button. */
export function isEffectsSupported() {
  const c = checkVideoEffectsCompatibility();
  return c.backgroundSupported || c.beautySupported;
}

/**
 * Load an <img> for a background image (custom data URL or same-origin
 * preset). Rejects on failure so callers can gracefully fall back to Off.
 */
export function loadBackgroundImage(url) {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error("No background image url"));
      return;
    }
    const img = new Image();
    // Same-origin presets (relative /backgrounds/*) and data: URLs don't need
    // crossOrigin; only remote http(s) URLs do, to avoid tainting the canvas.
    if (/^https?:\/\//i.test(url)) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Custom background failed to load"));
    img.src = url;
  });
}

/**
 * Idempotently create + pipe the processor chain for a given camera track:
 *   capture → beauty → virtual-background → destination
 * Returns opaque `handles` for applyEffects/disposeEffects.
 *
 * onOverload receives which effect overloaded: "background" | "beauty".
 */
export async function createEffects(videoTrack, { onOverload } = {}) {
  ensureRegistered();
  const existing = processorsByTrack.get(videoTrack);
  if (existing) return existing;

  const beauty = _beautyExt.createProcessor();
  const vb = _vbExt.createProcessor();
  await vb.init();
  if (onOverload) {
    vb.onoverload = () => onOverload("background");
    beauty.onoverload = () => onOverload("beauty");
  }
  videoTrack.pipe(beauty).pipe(vb).pipe(videoTrack.processorDestination);
  const handles = { track: videoTrack, beauty, vb, beautyOn: false, vbOn: false };
  processorsByTrack.set(videoTrack, handles);
  return handles;
}

/** Apply a full settings object to an existing handle set (idempotent). */
export async function applyEffects(handles, settings) {
  if (!handles) return;
  const { vb, beauty } = handles;
  const s = { ...DEFAULT_VIDEO_EFFECTS, ...(settings || {}) };

  // Background (virtual-background processor)
  if (s.background === "blur") {
    vb.setOptions({ type: "blur", blurDegree: s.blurDegree || 2 });
    if (!handles.vbOn) { await vb.enable(); handles.vbOn = true; }
  } else if (s.background === "image" && s.imageEl) {
    vb.setOptions({ type: "img", source: s.imageEl, fit: "cover" });
    if (!handles.vbOn) { await vb.enable(); handles.vbOn = true; }
  } else {
    if (handles.vbOn) { await vb.disable(); handles.vbOn = false; }
  }

  // Beauty processor
  if (s.beautyEnabled) {
    beauty.setOptions({
      lighteningContrastLevel: 1,
      lighteningLevel: s.lighteningLevel,
      smoothnessLevel: s.smoothnessLevel,
      sharpnessLevel: s.sharpnessLevel,
      rednessLevel: s.rednessLevel,
    });
    if (!handles.beautyOn) { await beauty.enable(); handles.beautyOn = true; }
  } else {
    if (handles.beautyOn) { await beauty.disable(); handles.beautyOn = false; }
  }
}

/** Tear down the processor chain and release WASM/worker resources. */
export async function disposeEffects(handles) {
  if (!handles) return;
  try { if (handles.beautyOn) await handles.beauty.disable(); } catch {}
  try { if (handles.vbOn) await handles.vb.disable(); } catch {}
  try { handles.track?.unpipe?.(); } catch {}
  try { await handles.beauty?.release?.(); } catch {}
  try { await handles.vb?.release?.(); } catch {}
  try { if (handles.track) processorsByTrack.delete(handles.track); } catch {}
}
